import { useState, useEffect } from 'react';
import { Card, Button, Badge, Input } from '@atoms/index';
import { apiClient } from '@services/api';
import Toast from '@/components/atoms/Toast';
import { useImageUpload } from '@/hooks/useImageUpload';

interface CuisineCategory {
  id: string;
  name: string;
  slug: string;
  imageUrl: string | null;
  displayOrder: number;
  isActive: boolean;
  keywords: string[] | null;
  createdAt: string;
}

const emptyForm = {
  name: '',
  slug: '',
  imageUrl: '',
  displayOrder: 0,
  isActive: true,
  keywords: '',
};

function toSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/ç/g, 'c').replace(/ğ/g, 'g').replace(/ı/g, 'i')
    .replace(/ö/g, 'o').replace(/ş/g, 's').replace(/ü/g, 'u')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

export const CuisineCategoriesTab = () => {
  const [cuisines, setCuisines] = useState<CuisineCategory[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [form, setForm] = useState({ ...emptyForm });
  const [isSaving, setIsSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [toast, setToast] = useState<{
    isVisible: boolean;
    message: string;
    type: 'success' | 'error';
  }>({ isVisible: false, message: '', type: 'success' });

  const imageUpload = useImageUpload({ folder: 'cuisine-categories' });

  useEffect(() => {
    loadCuisines();
  }, []);

  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({ isVisible: true, message, type });
  };

  const loadCuisines = async () => {
    setIsLoading(true);
    try {
      const res = await apiClient.get<{ success: boolean; data: CuisineCategory[] }>('/admin/cuisine-categories');
      setCuisines(res.data.data ?? []);
    } catch {
      showToast('Mutfaklar yüklenemedi', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const openCreate = () => {
    setForm({ ...emptyForm });
    setEditingId(null);
    setIsCreating(true);
  };

  const openEdit = (c: CuisineCategory) => {
    setForm({
      name: c.name,
      slug: c.slug,
      imageUrl: c.imageUrl ?? '',
      displayOrder: c.displayOrder,
      isActive: c.isActive,
      keywords: (c.keywords ?? []).join(', '),
    });
    setEditingId(c.id);
    setIsCreating(false);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setIsCreating(false);
  };

  const save = async () => {
    if (!form.name.trim() || !form.slug.trim()) {
      showToast('Ad ve slug zorunludur', 'error');
      return;
    }
    setIsSaving(true);
    try {
      const keywords = form.keywords
        .split(',')
        .map((k) => k.trim().toLowerCase())
        .filter(Boolean);

      const payload = {
        name: form.name.trim(),
        slug: form.slug.trim(),
        imageUrl: form.imageUrl.trim() || undefined,
        displayOrder: Number(form.displayOrder),
        isActive: form.isActive,
        keywords: keywords.length > 0 ? keywords : undefined,
      };

      if (editingId) {
        await apiClient.patch(`/admin/cuisine-categories/${editingId}`, payload);
        showToast('Mutfak kategorisi güncellendi', 'success');
      } else {
        await apiClient.post('/admin/cuisine-categories', payload);
        showToast('Mutfak kategorisi oluşturuldu', 'success');
      }
      await loadCuisines();
      cancelEdit();
    } catch {
      showToast('İşlem başarısız', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const deleteCuisine = async (id: string) => {
    if (!window.confirm('Bu mutfak kategorisini silmek istediğinize emin misiniz?')) return;
    setDeletingId(id);
    try {
      await apiClient.delete(`/admin/cuisine-categories/${id}`);
      showToast('Silindi', 'success');
      await loadCuisines();
    } catch {
      showToast('Silinemedi', 'error');
    } finally {
      setDeletingId(null);
    }
  };

  const formPanel = (
    <Card padding="lg" className="space-y-4">
      <h3 className="text-lg font-bold text-gray-900">
        {editingId ? 'Mutfak Kategorisi Düzenle' : 'Yeni Mutfak Kategorisi'}
      </h3>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Ad *</label>
          <Input
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value, slug: form.slug || toSlug(e.target.value) })}
            placeholder="Kebap & Izgara"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Slug *</label>
          <Input
            value={form.slug}
            onChange={(e) => setForm({ ...form, slug: e.target.value })}
            placeholder="kebap-izgara"
          />
          <p className="text-xs text-gray-400 mt-1">Sadece küçük harf, rakam ve tire</p>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Görsel</label>
          {/* Hidden file input */}
          <input
            ref={imageUpload.fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={(e) => imageUpload.handleFileChange(e, (url) => setForm({ ...form, imageUrl: url }))}
          />
          <div className="flex gap-2">
            <Input
              value={form.imageUrl}
              onChange={(e) => setForm({ ...form, imageUrl: e.target.value })}
              placeholder="https://... veya dosya yükleyin"
              className="flex-1"
            />
            <button
              type="button"
              onClick={imageUpload.triggerFilePicker}
              disabled={imageUpload.isUploading}
              className="flex-shrink-0 px-3 py-2 rounded-xl border border-gray-200 bg-white text-sm text-gray-600 hover:bg-gray-50 hover:border-gray-300 transition-colors disabled:opacity-50 whitespace-nowrap"
            >
              {imageUpload.isUploading ? '⏳ Yükleniyor…' : '📁 Dosya Seç'}
            </button>
          </div>
          {imageUpload.uploadError && (
            <p className="text-xs text-red-500 mt-1">{imageUpload.uploadError}</p>
          )}
          {form.imageUrl && (
            <img
              src={form.imageUrl}
              alt="önizleme"
              className="mt-2 h-14 w-14 rounded-xl object-cover border border-gray-200"
              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
            />
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Sıra</label>
          <Input
            type="number"
            min="0"
            value={String(form.displayOrder)}
            onChange={(e) => setForm({ ...form, displayOrder: Number(e.target.value) })}
          />
        </div>
        <div className="sm:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Anahtar kelimeler (virgülle ayırın)
          </label>
          <Input
            value={form.keywords}
            onChange={(e) => setForm({ ...form, keywords: e.target.value })}
            placeholder="kebap, adana, izgara, grill"
          />
          <p className="text-xs text-gray-400 mt-1">
            Restoranları bu mutfak kategorisiyle eşleştirmek için kullanılır
          </p>
        </div>
        <div className="flex items-center gap-3">
          <input
            id="cuisineActive"
            type="checkbox"
            checked={form.isActive}
            onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
            className="w-4 h-4 text-primary rounded"
          />
          <label htmlFor="cuisineActive" className="text-sm font-medium text-gray-700">Aktif</label>
        </div>
      </div>

      {form.imageUrl && (
        <div className="flex items-center gap-4 p-3 bg-gray-50 rounded-xl">
          <img
            src={form.imageUrl}
            alt={form.name}
            className="w-16 h-16 rounded-xl object-cover border border-gray-200"
            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
          />
          <div>
            <p className="font-semibold text-gray-900">{form.name || 'Önizleme'}</p>
            <p className="text-xs text-gray-400">{form.slug}</p>
          </div>
        </div>
      )}

      <div className="flex gap-3 pt-2">
        <Button variant="primary" onClick={save} isLoading={isSaving} className="rounded-xl">
          {editingId ? 'Güncelle' : 'Oluştur'}
        </Button>
        <Button variant="secondary" onClick={cancelEdit} className="rounded-xl">
          İptal
        </Button>
      </div>
    </Card>
  );

  return (
    <div className="space-y-6">
      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.isVisible}
        onClose={() => setToast({ ...toast, isVisible: false })}
      />

      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Mutfak Kategorileri</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            Mobil ve web uygulamasında görünen mutfak filtrelerini yönetin
          </p>
        </div>
        {!isCreating && !editingId && (
          <Button variant="primary" onClick={openCreate} className="rounded-xl font-semibold">
            + Yeni Mutfak
          </Button>
        )}
      </div>

      {(isCreating || editingId) && formPanel}

      {isLoading ? (
        <div className="text-center py-16 text-gray-400">Yükleniyor...</div>
      ) : cuisines.length === 0 ? (
        <Card padding="lg" className="text-center py-16 text-gray-500">
          <p className="font-medium mb-1">Henüz mutfak kategorisi eklenmemiş</p>
          <p className="text-sm">Yeni kategori oluşturarak başlayın.</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {cuisines.map((c) => (
            <Card key={c.id} padding="none" className="overflow-hidden flex flex-col">
              {c.imageUrl ? (
                <div className="aspect-[4/3] bg-gray-100">
                  <img
                    src={c.imageUrl}
                    alt={c.name}
                    className="w-full h-full object-cover"
                    onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                  />
                </div>
              ) : (
                <div className="aspect-[4/3] bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center text-4xl">
                  🍽️
                </div>
              )}
              <div className="p-4 flex-1 flex flex-col">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <h4 className="font-bold text-gray-900 text-sm">{c.name}</h4>
                  <Badge variant={c.isActive ? 'success' : 'secondary'} className="text-xs rounded-lg flex-shrink-0">
                    {c.isActive ? 'Aktif' : 'Pasif'}
                  </Badge>
                </div>
                <p className="text-xs text-gray-400 mb-2">{c.slug}</p>
                {c.keywords && c.keywords.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-3">
                    {c.keywords.slice(0, 4).map((kw) => (
                      <span key={kw} className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                        {kw}
                      </span>
                    ))}
                    {(c.keywords.length > 4) && (
                      <span className="text-xs text-gray-400">+{c.keywords.length - 4}</span>
                    )}
                  </div>
                )}
                <div className="flex items-center justify-between mt-auto pt-2 border-t border-gray-100">
                  <span className="text-xs text-gray-400">Sıra: {c.displayOrder}</span>
                  <div className="flex gap-1">
                    <button
                      type="button"
                      onClick={() => openEdit(c)}
                      className="p-1.5 rounded-lg text-gray-500 hover:bg-primary/10 hover:text-primary transition-colors"
                    >
                      ✏️
                    </button>
                    <button
                      type="button"
                      onClick={() => deleteCuisine(c.id)}
                      disabled={deletingId === c.id}
                      className="p-1.5 rounded-lg text-gray-500 hover:bg-red-50 hover:text-red-600 transition-colors"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};
